import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { withPropsOnChange } from 'recompose';
import { formatUSD } from '@sparefoot/react-core/utils/format';
import { facilityUrl } from 'utils/routes/url';

import { IconText } from 'components/core/IconText';
import { Link } from 'components/core/Link';
import { Button } from 'components/core/Button';
import { H4, H3, Paragraph } from 'components/core/Typography';
import { MapPin, Money, Promo } from 'components/svg';
import { FacilityImage } from 'components/facility/FacilityImage';
import { StarRating } from 'components/review/StarRating';
import { UnitIcon } from 'components/unit/UnitIcon';
import { stateAbbreviationToFullName } from 'utils/formatting';

import './BestPricedUnits.scss';

const sizeBuckets = ['5x5', '10x10', '10x15', '10x20'];

export const enhance = withPropsOnChange(
	['pricingStats'],
	({ pricingStats, location }) => ({
		units: sizeBuckets.reduce((units, size) => {
			const lowestPricedUnit = get(pricingStats, ['lowestPrices', size]);
			const averagePrice = get(pricingStats, [
				'averagePrices',
				size,
				'price',
			]);
			if (get(lowestPricedUnit, 'price', null) && averagePrice) {
				return [
					...units,
					{
						...lowestPricedUnit,
						sizeText: size,
						averagePrice,
						reserveUrl: facilityUrl({
							seoLocation: {
								...location,
								city:
									lowestPricedUnit?.facilityCity ||
									location.city,
								fullState:
									stateAbbreviationToFullName(
										lowestPricedUnit?.facilityState,
									) || location.fullState,
							},
							id: lowestPricedUnit.facility,
							name: lowestPricedUnit.facilityName,
							inNetwork: get(
								lowestPricedUnit,
								'inNetwork',
								false,
							),
						}),
					},
				];
			}

			return units;
		}, []),
	}),
);

export function BestPricedUnits({ location, units }) {
	if (!units) return null;
	return (
		<div className="best-priced-units">
			<H3 size="medium">
				Cheapest Self Storage in {location.city}, {location.state}
			</H3>
			<Paragraph>
				A storage unit's price is based on several factors, including
				the facility's amenities (such as 24/7 access, free use of
				truck), the storage unit's amenities (such as climate control,
				electrical outlet), facility location, unit location (a ground
				floor unit will be more expensive), and overall demand in your
				area.
			</Paragraph>
			<Paragraph>
				When looking at pricing for storage units in {location.city},
				it's important to think about rental rates in terms of price for
				value. Sometimes, the cheapest storage unit doesn't always mean
				it's the best unit for YOUR needs.
			</Paragraph>
			{!!units.length && (
				<ul className="best-unit-list">
					{units.map((unit) => (
						<li
							key={unit.unit}
							className="best-unit-item"
						>
							<H4 size="medium">
								Cheapest {unit.sizeText} Storage Unit in{' '}
								{location.city}, {location.state}{' '}
							</H4>
							<Paragraph>
								If you're looking for a cheap {unit.sizeText}{' '}
								unit in {location.city}, you'll get the best
								value by renting a {unit.price} unit at{' '}
								{unit.facilityName}.
							</Paragraph>
							<Link
								to={unit.reserveUrl}
								className="new-unit-card"
							>
								<div className="unit-head">
									<FacilityImage
										src={unit.facilityImage}
										size="medium"
										lazyLoad
									/>
									<div className="facility-head-content">
										<H4
											className="facility-name"
											color="black"
											last
											itemProp="name"
										>
											{unit.facilityName}
										</H4>
										<IconText
											icon={MapPin}
											iconFill="#666666"
											type="inline-text"
											className="icon-text"
										>
											<Paragraph
												size="tiny"
												weight="standard"
												className="facility-location"
												itemProp="address"
												last
												color="gray"
											>
												{`${unit.facilityCity} ${unit.facilityState} ${location.zip ? location.zip : ''}`}
											</Paragraph>
										</IconText>
									</div>
								</div>
								<div className="unit-photo">
									<UnitIcon
										squareFootage={unit.squareFootage}
									/>
								</div>
								<div className="unit-card-bottom">
									<div className="unit-head">
										<div className="price-icon">
											<Money
												className="money-icon"
												fill="#FFFFFF"
											/>
										</div>
										<div className="facility-head-content">
											<H3
												className="facility-name"
												color="black"
												last
												itemProp="name"
											>
												{`${unit.sizeText} unit `}{' '}
												<span
													style={{ color: '#666666' }}
												>
													•
												</span>{' '}
												{`$${unit.price} monthly`}
											</H3>
											<IconText
												icon={Promo}
												type="inline-text"
												className="icon-text"
											>
												<Paragraph
													size="tiny"
													weight="standard"
													className="facility-location"
													itemProp="address"
													last
													color="gray"
												>
													{formatUSD(
														unit.averagePrice -
															unit.price,
													)}{' '}
													under average price
												</Paragraph>
											</IconText>
										</div>
									</div>
									<div className="unit-rating">
										<div className="star-rating">
											<StarRating
												rating={5}
												outOf={5}
												dollar
											/>
											<Paragraph
												last
												size="tiny"
												weight="bold"
												color="darkGray"
											>
												5/5 Price Rating
											</Paragraph>
										</div>
										<Button
											link
											underline
										>
											<Paragraph
												last
												weight="bold"
												size="standard"
											>
												Book Now
											</Paragraph>
										</Button>
									</div>
								</div>
							</Link>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

BestPricedUnits.propTypes = {
	location: PropTypes.shape({
		zip: PropTypes.string,
		city: PropTypes.string,
		state: PropTypes.string,
	}).isRequired,
	units: PropTypes.arrayOf(
		PropTypes.shape({
			lowestPricedUnit: PropTypes.object,
		}),
	),
};

BestPricedUnits.defaultProps = {
	units: [],
};

export default enhance(BestPricedUnits);
